import React, { useEffect, useRef, useState } from "react";
import { Package, Hand, ArrowLeftRight, Home } from "lucide-react";
import { Hud, Pop, POP_CSS, useCountdown } from "../Hud";
import { roundBtn, GLASS, SAFE_BOTTOM, FONT } from "../../kota/ui";
import { JalanRaya, LAJUR, RIDER_Y } from "./JalanRaya";

const DURASI = 90, PAKET = 15, JANGKAUAN = 70, DENDA = { kerucut: 2, polisi_tidur: 1, mobil: 3 };
const NAMA_KOMPLEKS = ["MAWAR", "ANGGREK", "CEMPAKA", "MELATI", "KENANGA", "FLAMBOYAN"];

// Mini game Ojol — Lempar Paket (ala Thaloria): jalan bergulir, kurir di tengah. Rumah berpenanda paket lewat di kiri/kanan —
// ketuk tombol lempar sisi itu saat rumah sejajar kurir (±70 px) → +1. Lempar tanpa rumah → paket terbuang. Rintangan di lajur
// (kerucut −2, polisi tidur −1, mobil −3) dihindari dengan tombol pindah lajur. 15 paket, 90 dtk. Skor = poin / 15 × 100.
// Level ↑ → jalan lebih cepat & rintangan lebih rapat. Dunia deterministik dari seed shift.
export function LemparPaket({ shift, onSelesai }) {
  const lv = shift.level || 1;
  const KECEPATAN = 150 + (lv - 1) * 10, JEDA_RINTANGAN = Math.max(260, 420 - (lv - 1) * 20);
  const H = window.innerHeight, RIDER_PX = H * RIDER_Y;
  const r = useRef(null);
  if (!r.current) {
    let n = 0;
    const rnd = () => { const x = Math.sin(shift.seed + (n++) * 97.3) * 10000; return x - Math.floor(x); };
    r.current = { rnd, items: [], dist: 0, nextLot: 260, nextRintangan: 700, nextSpanduk: 1400, id: 0, poin: 0, terkirim: 0, paket: PAKET, lane: 0, selesai: false, last: 0, kena: 0, k: 0 };
  }
  const [frame, setFrame] = useState({ items: [], lane: 0, kena: false });
  const [stat, setStat] = useState({ poin: 0, paket: PAKET });
  const [pops, setPops] = useState([]);
  const pop = (x, y, teks, warna) => setPops(ps => [...ps.slice(-4), { x, y, teks, warna, k: r.current.k++ }]);

  function akhiri() {
    const s = r.current;
    if (s.selesai) return;
    s.selesai = true;
    onSelesai(Math.max(0, Math.min(100, Math.round((s.poin / PAKET) * 100))));
  }
  const sisa = useCountdown(DURASI, akhiri);

  useEffect(() => {
    const s = r.current;
    let raf;
    const loop = now => {
      if (s.selesai) return;
      const dt = Math.min(0.05, (now - (s.last || now)) / 1000); s.last = now;
      s.dist += KECEPATAN * dt;
      // spawn kavling kiri & kanan
      while (s.nextLot < s.dist + H + 200) {
        for (const sisi of ["kiri", "kanan"]) {
          const u = s.rnd();
          if (u < 0.62) s.items.push({ id: s.id++, jenis: "rumah", sisi, wy: s.nextLot + s.rnd() * 30, warna: Math.floor(s.rnd() * 6), paket: s.rnd() < 0.42, terkirim: false });
          else if (u < 0.85) s.items.push({ id: s.id++, jenis: "pohon", sisi, wy: s.nextLot + s.rnd() * 60, warna: Math.floor(s.rnd() * 3) });
        }
        s.nextLot += 150;
      }
      while (s.nextRintangan < s.dist + H + 200) {
        const u = s.rnd();
        s.items.push({ id: s.id++, jenis: u < 0.45 ? "kerucut" : u < 0.75 ? "polisi_tidur" : "mobil", lane: s.rnd() < 0.5 ? 0 : 1, wy: s.nextRintangan, warna: Math.floor(s.rnd() * 2), kena: false });
        s.nextRintangan += JEDA_RINTANGAN + s.rnd() * 200;
      }
      if (s.nextSpanduk < s.dist + H + 200) { s.items.push({ id: s.id++, jenis: "spanduk", wy: s.nextSpanduk, nama: NAMA_KOMPLEKS[Math.floor(s.rnd() * NAMA_KOMPLEKS.length)] }); s.nextSpanduk += 1600; }
      // posisi layar + tabrakan (mobil ikut melaju searah, lebih lambat dari kurir → tampak didekati pelan)
      for (const it of s.items) {
        if (it.jenis === "mobil") it.wy += KECEPATAN * 0.55 * dt;
        it.top = RIDER_PX - (it.wy - s.dist);
        if (DENDA[it.jenis] && !it.kena && it.lane === s.lane && Math.abs(it.top - RIDER_PX) < 26) {
          it.kena = true; s.poin = Math.max(0, s.poin - DENDA[it.jenis]); s.kena = now;
          pop(LAJUR[s.lane], RIDER_PX - 50, `-${DENDA[it.jenis]}`, "#ff8a8a"); setStat({ poin: s.poin, paket: s.paket });
        }
      }
      s.items = s.items.filter(it => it.top < H + 160);
      setFrame({ items: s.items.slice(), lane: s.lane, kena: now - s.kena < 350 });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []); // eslint-disable-line

  function lempar(sisi) {
    const s = r.current;
    if (s.selesai || s.paket <= 0) return;
    s.paket -= 1;
    const target = s.items.find(it => it.jenis === "rumah" && it.sisi === sisi && it.paket && !it.terkirim && Math.abs(it.top - RIDER_PX) < JANGKAUAN);
    const x = sisi === "kiri" ? 14 : 86;
    if (target) { target.terkirim = true; s.poin += 1; s.terkirim += 1; pop(x, RIDER_PX - 40, "+1 Terkirim!", "#ffe08a"); }
    else pop(x, RIDER_PX - 40, "Meleset", "#ffb4b4");
    setStat({ poin: s.poin, paket: s.paket });
    if (s.paket <= 0) setTimeout(akhiri, 900);
  }
  const pindah = () => { const s = r.current; if (!s.selesai) s.lane = s.lane ? 0 : 1; };

  const btn = { ...roundBtn(false), width: 64, height: 64, pointerEvents: "auto", position: "relative", WebkitTapHighlightColor: "transparent", touchAction: "manipulation" };
  return (
    <>
      <style>{POP_CSS}</style>
      <JalanRaya items={frame.items} laneX={LAJUR[frame.lane]} riderY={RIDER_PX} kena={frame.kena}>
        {pops.map(p => <Pop key={p.k} x={`${p.x}%`} y={p.y} warna={p.warna}>{p.teks}</Pop>)}
      </JalanRaya>
      <Hud sisa={sisa} durasi={DURASI} poin={stat.poin} extra={[[Package, "#e07a1f", `${stat.paket}/${PAKET}`]]} />
      <div data-testid="kontrol-ojol" style={{ position: "absolute", left: 0, right: 0, bottom: `calc(${SAFE_BOTTOM} + 22px)`, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 22px", pointerEvents: "none", fontFamily: FONT }}>
        <button data-testid="lempar-kiri" aria-label="Lempar paket ke kiri" onPointerDown={() => lempar("kiri")} style={btn}>
          <Hand size={26} color="#e07a1f" /><span style={{ position: "absolute", top: -6, right: -6, width: 24, height: 24, borderRadius: 99, background: "#fff", border: "1px solid var(--g-border)", display: "grid", placeItems: "center" }}><Home size={13} color={GLASS.accent} /></span>
        </button>
        <button data-testid="pindah-lajur" aria-label="Pindah lajur" onPointerDown={pindah} style={{ ...btn, width: 72, height: 56, borderRadius: 20 }}><ArrowLeftRight size={26} color={GLASS.accent} /></button>
        <button data-testid="lempar-kanan" aria-label="Lempar paket ke kanan" onPointerDown={() => lempar("kanan")} style={btn}>
          <Hand size={26} color="#e07a1f" style={{ transform: "scaleX(-1)" }} /><span style={{ position: "absolute", top: -6, left: -6, width: 24, height: 24, borderRadius: 99, background: "#fff", border: "1px solid var(--g-border)", display: "grid", placeItems: "center" }}><Home size={13} color={GLASS.accent} /></span>
        </button>
      </div>
    </>
  );
}
